import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faSave, faUndo } from '@fortawesome/free-solid-svg-icons';
import useDeliveryOrders from '../../hooks/useDeliveryOrders';

import { UI_COLORS, colorHelpers } from '../../utils/colors';
import ConfirmDialog from '../common/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useCoilSettings } from '../../context/CoilSettingsContext';
import CoilPickStrip from '../coils/CoilPickStrip';
import { isPieceUnit } from '../../utils/units';

/** Whole kg — the database constraint on a coil weight, so a take never carries a fraction. */
const toKg = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? Math.round(num) : 0;
};

/** What a coil line actually loads: the sum of what each picked coil gives up. */
const sumTakeKg = (picks) => (Array.isArray(picks) ? picks : [])
    .reduce((sum, pick) => sum + toKg(pick?.take_kg), 0);

/**
 * The coils an order line already carries, in the shape CoilPickStrip expects.
 *
 * The API returns them as coil_allocations; the same reader as the order form, field for
 * field, because a mismatch here is silent — the strip would simply come up empty and the
 * salesperson's pick would look like it had never happened. The remaining/available
 * fallbacks matter for the same reason they do there: without them a reopened pick
 * measures itself against 0 kg available and reads as invalid the moment it is opened.
 */
const picksOfItem = (item) => {
    const allocations = item?.coil_allocations ?? item?.coil_picks;
    return (Array.isArray(allocations) ? allocations : []).map((allocation) => {
        const takeKg = allocation.take_kg ?? allocation.allocated_weight_kg ?? 0;
        return {
            coil_id: allocation.coil_id ?? allocation.id,
            take_kg: takeKg,
            batch_number: allocation.batch_number || '',
            colour: allocation.colour || '',
            kg_per_m: allocation.kg_per_m,
            remaining_weight_kg: allocation.remaining_weight_kg ?? takeKg,
            available_weight_kg: allocation.available_weight_kg ?? allocation.remaining_weight_kg ?? takeKg,
        };
    });
};

/**
 * Coil identity plus the weight taken off it is the whole of what the API needs — the
 * batch, colour and kg/m on a pick are the server's own data, echoed back only so the
 * strip can render without re-fetching. Zero-weight rows are dropped: they are a coil the
 * operator added and then emptied, not a coil that ships.
 */
const coilPicksPayload = (picks) => (Array.isArray(picks) ? picks : [])
    .filter((pick) => toKg(pick?.take_kg) > 0)
    .map((pick) => ({
        coil_id: Number(pick.coil_id),
        take_kg: toKg(pick.take_kg),
        // Forwarded only when the operator actually weighed the roll. This screen is
        // where that measurement happens, so dropping it here would discard the one
        // figure the weighbridge can genuinely produce.
        ...(pick.remainder_kg === undefined || pick.remainder_kg === null
            ? {}
            : { remainder_kg: toKg(pick.remainder_kg) }),
    }));

const CompleteTruckLoading = ({ isOpen, orderId, initialOrder = null, onClose, onCompleted }) => {
    const { t } = useTranslation();
    const { fetchOrderById, completeLoading, addWeightEntry, resetWeightEntries } = useDeliveryOrders();
    const { coilTrackingEnabled } = useCoilSettings();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [finalTruckWeight, setFinalTruckWeight] = useState('');
    const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
    const [itemsUpdate, setItemsUpdate] = useState([]);
    const [internalRemark, setInternalRemark] = useState('');

    // Multi-step weight entries state
    const [weightEntries, setWeightEntries] = useState([]);
    const [intermediateWeight, setIntermediateWeight] = useState('');
    const [showResetConfirm, setShowResetConfirm] = useState(false);


    const formStrings = t('deliveryOrdersModule.forms.completeTruckLoading', { returnObjects: true });

    // Two-level gate: the global setting AND this line's product size, both off by default.
    // With either off the line behaves exactly as it always did — no strip, no read-only
    // pieces, no coil arithmetic.
    const isCoilLine = useCallback(
        (item) => coilTrackingEnabled && !!item?.coil_tracked,
        [coilTrackingEnabled]
    );

    // Load order details when modal opens
    useEffect(() => {
        if (!isOpen || !orderId) {
            return;
        }

        let isMounted = true;

        const loadOrder = async () => {
            // Safety timeout
            const timeoutId = setTimeout(() => {
                if (isMounted) {
                    setLoading(false);
                    setError(formStrings.messages.loadingTimeout);
                }
            }, 10000);

            setLoading(true);
            setError(null);
            setOrder(null);
            setFinalTruckWeight('');
            setEmptyTruckWeight('');
            setItemsUpdate([]);
            setWeightEntries([]);
            setIntermediateWeight('');
            setShowResetConfirm(false);
            setInternalRemark('');

            try {
                let orderData = initialOrder;
                // Fetch if no initial data or if we want fresh data (which allows for 'refresh' behavior)
                // Generally good to fetch fresh data for critical actions
                if (!orderData) {
                    const result = await fetchOrderById(orderId);
                    if (result && result.success && result.data) orderData = result.data;
                    else if (isMounted) setError(result?.error || formStrings.messages.invalidOrder);
                }

                if (!orderData) {
                    // Error set above if fetch failed
                    return;
                }

                if (isMounted) {
                    setOrder(orderData);

                    // Initialize items update array with current quantities
                    if (Array.isArray(orderData.items)) {
                        const updates = orderData.items.map((item, index) => ({
                            index,
                            actualQuantity: (item.actual_quantity !== null && typeof item.actual_quantity !== 'undefined') ? String(item.actual_quantity) : '',
                            actualPieces: '', // Stop pre-filling, loader must manually enter
                            perPieceWeight: (item.per_piece_weight !== null && typeof item.per_piece_weight !== 'undefined') ? String(item.per_piece_weight) : '',
                            // The coils picked at order time, loaded as the starting point:
                            // loading revises that pick, it does not start from nothing.
                            coilPicks: picksOfItem(item),
                            remark: item.remark || '',
                        }));
                        setItemsUpdate(updates);
                    }

                    // Set initial weights if available
                    if (orderData.final_truck_weight) setFinalTruckWeight(String(orderData.final_truck_weight));
                    if (orderData.internal_remark) setInternalRemark(orderData.internal_remark);
                    if (orderData.empty_truck_weight) setEmptyTruckWeight(String(orderData.empty_truck_weight));

                    // Load existing weight entries if any
                    if (orderData.weight_entries_list && Array.isArray(orderData.weight_entries_list)) {
                        setWeightEntries(orderData.weight_entries_list);
                    }
                }
            } catch (err) {
                console.error('Failed to load order:', err);
                if (isMounted) setError(formStrings.messages.invalidOrder);
            } finally {
                clearTimeout(timeoutId);
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        const timer = setTimeout(() => {
            loadOrder();
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderId, initialOrder, fetchOrderById, formStrings.messages.invalidOrder]);

    // Effect to pre-fill perPieceWeight if not set, once productSizes are available
    // Effect to pre-fill perPieceWeight if not set, using item standard details
    useEffect(() => {
        if (!order || !order.items || itemsUpdate.length === 0) return;

        setItemsUpdate(prevUpdates => {
            let changed = false;
            const newUpdates = prevUpdates.map((update, idx) => {
                // If perPieceWeight is already set (user input or from order), skip
                if (update.perPieceWeight && parseFloat(update.perPieceWeight) > 0) return update;

                const item = order.items[idx];
                if (!item) return update;

                // A coil has no per-piece weight — its kg come off the scale, one coil at a
                // time — and the field is hidden for it, so leave it empty.
                if (isCoilLine(item)) return update;

                // Use standard weight and length from the item itself
                const len = parseFloat(item.length) || 0;
                const pWeight = parseFloat(item.standard_weight) || 0;
                const sLength = parseFloat(item.standard_length) || 1;

                // If the item has a standard weight directly (like a pipe), use it with length adjustment
                // Formula: (Actual Length * Standard Weight) / Standard Length
                const effectiveLen = len > 0 ? len : sLength;
                const theoreticalW = (effectiveLen * pWeight) / sLength;

                if (theoreticalW > 0) {
                    changed = true;
                    return {
                        ...update,
                        perPieceWeight: theoreticalW.toFixed(3) // Pre-fill with 3 decimals
                    };
                }

                return update;
            });

            return changed ? newUpdates : prevUpdates;
        });
    }, [order, itemsUpdate.length, isCoilLine]);

    // Computed: Items already saved in a weight entry (for display context)
    const savedItemStats = useMemo(() => {
        const stats = new Map(); // id -> { pieces, weight }
        weightEntries.forEach(entry => {
            // If the entry has item_updates (rich data), prefer that
            if (entry.items_update_details) {
                entry.items_update_details.forEach(detail => {
                    const prev = stats.get(detail.id) || { pieces: 0, weight: 0 };
                    // Handle both field names used by different API versions or serialization
                    const weightVal = parseFloat(detail.actual_weight || detail.actual_quantity || 0);
                    stats.set(detail.id, {
                        pieces: prev.pieces + (detail.actual_pieces || 0),
                        weight: prev.weight + weightVal
                    });
                });
            } else {
                // Fallback: distribute weight_diff if details missing (legacy or simple entries)
                const theoreticalSum = order?.items
                    ?.filter(i => entry.item_ids.includes(i.id))
                    ?.reduce((sum, item) => {
                        const idx = order.items.findIndex(it => it.id === item.id);
                        const pWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || parseFloat(item.per_piece_weight) || 0;
                        const pieces = item.actual_pieces || 0; // Use what's in the item object from API
                        return sum + (pieces * pWeight);
                    }, 0) || 0;

                entry.item_ids.forEach(id => {
                    const prev = stats.get(id) || { pieces: 0, weight: 0 };
                    const item = order?.items?.find(i => i.id === id);
                    const idx = order?.items?.findIndex(i => i.id === id);
                    const pWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || parseFloat(item?.per_piece_weight) || 0;
                    const pieces = item?.actual_pieces || 0;
                    
                    const ratio = theoreticalSum > 0 ? (pieces * pWeight) / theoreticalSum : (1 / entry.item_ids.length);
                    stats.set(id, {
                        pieces: prev.pieces + pieces,
                        weight: prev.weight + (entry.weight_diff * ratio)
                    });
                });
            }
        });
        return stats;
    }, [weightEntries, order, itemsUpdate]);

    const modifiedItemIds = useMemo(() => {
        const ids = [];
        order?.items?.forEach((item, idx) => {
            const update = itemsUpdate[idx];
            
            const savedStats = savedItemStats.get(item.id) || { pieces: 0, weight: 0 };
            const isFullyLoaded = savedStats.pieces > 0;

            // A coil line has no pieces to type — the pick IS the quantity — so it counts as
            // modified as soon as one coil with a real take sits on it in this cycle.
            // Without this branch "Save (N)" never arms for a coil line and the revision
            // made here cannot be saved at all.
            const hasCoilPick = isCoilLine(item) && sumTakeKg(update?.coilPicks) > 0;

            // Item is "modified" if actualPieces has been entered in CURRENT cycle
            // AND the item is NOT already fully loaded (blue)
            if (!isFullyLoaded && ((update?.actualPieces && parseFloat(update.actualPieces) > 0) || hasCoilPick)) {
                ids.push(item.id);
            }
        });
        return ids;
    }, [order, itemsUpdate, savedItemStats, isCoilLine]);

    const validateForm = () => {
        // Validate truck weight
        if (!finalTruckWeight || !finalTruckWeight.trim()) {
            setError(formStrings.messages.invalidWeight);
            return false;
        }

        const weight = parseFloat(finalTruckWeight);
        if (isNaN(weight) || weight <= 0) {
            setError(formStrings.messages.invalidWeight);
            return false;
        }

        // Validate empty truck weight
        if (!emptyTruckWeight || !emptyTruckWeight.trim()) {
            setError(formStrings.messages.invalidWeight);
            return false;
        }

        const eWeight = parseFloat(emptyTruckWeight);
        if (isNaN(eWeight) || eWeight < 0) {
            setError(formStrings.messages.invalidWeight);
            return false;
        }

        if (weight <= eWeight) {
            setError('Final truck weight must be greater than empty truck weight');
            return false;
        }

        // Validate pieces and weight (only for MODIFIED items if saving intermediate, or ALL if completing)
        // If completing, we don't strictly require all items to be filled anymore if they were loaded in previous steps
        let atLeastOneItemLoaded = weightEntries.length > 0;
        
        for (let i = 0; i < itemsUpdate.length; i++) {
            const update = itemsUpdate[i];
            const item = order.items[i];

            // A coil line is filled by its coils: pieces and per-piece weight are hidden for
            // it, so the checks below would never see it as loaded and a coil-only truck
            // could never be completed.
            if (isCoilLine(item)) {
                if (sumTakeKg(update?.coilPicks) > 0) atLeastOneItemLoaded = true;
                continue;
            }

            const piecesStr = String(update.actualPieces ?? '').trim();
            const weightStr = String(update.perPieceWeight ?? '').trim();

            const pieces = parseFloat(piecesStr);
            const pWeight = parseFloat(weightStr);

            if (piecesStr && piecesStr.trim() !== '') {
                // Pieces must be a valid, non-negative number.
                if (Number.isNaN(pieces) || pieces < 0) {
                    setError(`${formStrings.labels.productName} "${item.size_name || item.product_name}": ${formStrings.messages.invalidQuantity}`);
                    return false;
                }
                // Zero pieces = this size is intentionally NOT loaded on this truck. A
                // zero-delivered line is dropped from the challan (the backend skips
                // lines with actual_quantity <= 0), so it needs no per-piece weight —
                // this restores the long-standing "enter 0 to drop a size from the
                // challan" workflow instead of demanding a weight for a line that debits
                // nothing. (A blank Actual Pieces already skipped this block; this just
                // handles an explicit 0 the same way.)
                if (pieces === 0) {
                    continue;
                }
                // Loading a positive count: the per-piece weight must be present and
                // valid so the kg stock debit can convert pieces -> kg.
                if (!weightStr || Number.isNaN(pWeight) || pWeight < 0) {
                    setError(`${formStrings.labels.productName} "${item.size_name || item.product_name}": ${formStrings.messages.invalidQuantity}`);
                    return false;
                }
                // A per-piece weight of 0 is fine for a PIECES-TRACKED product: its
                // stock posts from the captured piece count, and the weight is only
                // for the weighbridge and loading cost. For a kg-tracked product it
                // is not — the challan would debit no stock at all — so say so here
                // rather than silently disabling Complete with no explanation.
                if (pWeight === 0 && !item.track_by_pieces && !isCoilLine(item)) {
                    setError(t('deliveryOrders.errors.perPieceWeightRequired', {
                        product: item.size_name || item.product_name,
                    }));
                    return false;
                }
                atLeastOneItemLoaded = true;
            }
        }

        if (!atLeastOneItemLoaded) {
            setError(formStrings.messages.noItems || 'Fill loaded quantity for at least one item');
            return false;
        }

        return true;
    };

    // Calculations - handles cumulative loading across multiple steps
    const calculatedData = React.useMemo(() => {
        if (!order || !order.items || itemsUpdate.length === 0) return { items: [], totalCost: 0, isValid: false };

        const emptyW = parseFloat(emptyTruckWeight) || 0;
        const finalW = parseFloat(finalTruckWeight) || 0;

        // 1. Calculate theoretical weights for current cycle's inputs
        const itemCalcs = order.items.map((item, idx) => {
            const isCoil = isCoilLine(item);
            // Already banked in a weight entry: this cycle adds nothing more for it, the same
            // way a saved ordinary line has its pieces input cleared. The picks stay on
            // screen for reference but must not be counted a second time.
            const banked = (savedItemStats.get(item.id)?.pieces || 0) > 0;
            const currentPicks = (isCoil && !banked) ? (itemsUpdate[idx]?.coilPicks || []) : [];

            const currentActualPieces = isCoil
                ? currentPicks.length
                : (parseFloat(itemsUpdate[idx]?.actualPieces) || 0);
            const perPieceWeight = parseFloat(itemsUpdate[idx]?.perPieceWeight) || 0;
            const sLength = (parseFloat(item.length) || parseFloat(item.standard_length)) || 0;
            // Metres are a per-coil attribute, never the quantity a coil line settles in —
            // a coil is billed in kg like every other product.
            const isMeter = !isCoil && item.unit?.toLowerCase() === 'm';

            // A coil line weighs the sum of what its picked coils give up; pieces and
            // per-piece weight do not enter into it.
            const theoreticalW = isCoil
                ? sumTakeKg(currentPicks)
                : (currentActualPieces * perPieceWeight);
            const actualQty = isMeter
                ? (currentActualPieces * sLength)
                : theoreticalW;

            // Debugging log if needed
            // console.log(`Item ${idx}: pieces=${currentActualPieces}, theoreticalW=${theoreticalW}, actualQty=${actualQty}`);

            return {
                ...item,
                isCoil,
                currentPicks,
                currentActualPieces,
                actualQty,
                theoreticalW,
                isMeter
            };
        });

        // 2. Determine adjustment factor for current cycle
        // Difference between current truck weight (finalW) and last recorded weight
        let lastTruckWeight = emptyW;
        if (weightEntries.length > 0) {
            lastTruckWeight = Math.max(...weightEntries.map(e => e.truck_weight));
        }
        const rawWeightDiff = finalW > 0 ? Math.max(0, finalW - lastTruckWeight) : 0;

        // THE TRUCK IS THE FINAL WORD. Coil lines divide the weighbridge difference along
        // with every other line — the yard has always settled a discrepancy that way —
        // and the server then brings each line's picks up to the number that settled, so
        // the coils give up exactly what the challan bills.
        const currentWeightDiff = rawWeightDiff;

        const currentTheoreticalSum = itemCalcs.reduce((sum, item) => sum + item.theoreticalW, 0);
        const adjustmentFactor = (currentWeightDiff > 0 && currentTheoreticalSum > 0)
            ? currentWeightDiff / currentTheoreticalSum
            : 1;

        // 3. Final Weights & Costs
        const loadingCostPerKg = parseFloat(order.loading_cost_per_kg) || 0;
        let totalCost = 0;
        
        // Ensure the weight difference we are distributing is also integer-based if "no decimals" is preferred
        const finalWeightDiff = Math.round(currentWeightDiff);
        let distributedCurrentWeight = 0;
        // Coil lines are adjustable like any other: the truck settles what shipped, and
        // the server then brings each line's picks to that number so the coils give up
        // exactly what is billed.
        const adjustableItems = itemCalcs.filter(it => it.theoreticalW > 0);
        const lastAdjustableItem = adjustableItems[adjustableItems.length - 1];

        const finalItems = itemCalcs.map((item, index) => {
            const currentActualPieces = item.currentActualPieces;
            const savedStats = savedItemStats.get(item.id) || { pieces: 0, weight: 0 };

            let currentAdjustedW = 0;
            if (finalW > 0 && item.theoreticalW > 0) {
                if (item === lastAdjustableItem) {
                    currentAdjustedW = Math.max(0, finalWeightDiff - distributedCurrentWeight);
                } else {
                    // Round to whole integers as requested
                    currentAdjustedW = Math.round(item.theoreticalW * adjustmentFactor);
                    distributedCurrentWeight += currentAdjustedW;
                }
            } else if (item.theoreticalW > 0) {
                currentAdjustedW = Math.round(item.theoreticalW); // Use theoretical for preview
            }

            const totalAdjustedW = savedStats.weight + currentAdjustedW;
            const totalActualPieces = savedStats.pieces + currentActualPieces;
            
            const cost = totalAdjustedW * loadingCostPerKg;
            totalCost += cost;

            // Accurate tracking based on backend fields, with fallback to saved stats
            const sLength = (parseFloat(item.length) || parseFloat(item.standard_length)) || 0;
            const isMeter = item.isMeter;
            // A pcs line is counted in PIECES end to end: actual_quantity holds the
            // delivered count, the ordered figure is quantity_in_pieces, and this
            // cycle contributes its piece count. Mixing in kg here (as it did when
            // actual_quantity became a count) made every multi-trip pcs delivery
            // report a phantom shortfall of roughly the per-piece weight.
            const isPcsLine = !item.isCoil && isPieceUnit(item.unit);

            let prevDelivered = parseFloat(item.total_delivered_quantity);
            if (isNaN(prevDelivered) || prevDelivered === 0) {
                 prevDelivered = parseFloat(item.actual_quantity);
            }
            if (isNaN(prevDelivered) || prevDelivered === 0) {
                 prevDelivered = isPcsLine
                     ? savedStats.pieces
                     : (isMeter ? (savedStats.pieces * sLength) : savedStats.weight);
            }
            prevDelivered = prevDelivered || 0;

            const totalTarget = isPcsLine
                ? (parseFloat(item.quantity_in_pieces) || parseFloat(item.total_quantity) || 0)
                : (parseFloat(item.total_quantity) || parseFloat(item.quantity) || 0);
            const thisCycle = isPcsLine ? (item.currentActualPieces || 0) : (item.actualQty || 0);
            const totalDelivered = prevDelivered + thisCycle;
            const pendingQty = Math.max(0, totalTarget - totalDelivered);

            return {
                ...item,
                totalTarget,
                totalDelivered,
                pendingQty,
                savedPieces: savedStats.pieces,
                savedWeight: savedStats.weight,
                totalActualPieces,
                adjustedW: currentAdjustedW, 
                totalAdjustedW,
                cost
            };
        });

        const totalActualQty = finalItems.reduce((sum, item) => sum + (item.totalAdjustedW || 0), 0);
        const netTruckW = Math.round(Math.max(0, finalW - emptyW));

        return {
            items: finalItems,
            totalCost: Math.round(totalCost), // Round cost to whole number too
            totalActualQty,
            netTruckW,
            // A pieces-tracked line contributes 0 kg by design (stock posts from the
            // piece count), so a truck carrying only those summed to 0 and left
            // Complete disabled with nothing on screen to explain it.
            isValid: totalActualQty > 0
                || finalItems.some(i => i.track_by_pieces && (i.totalActualPieces || 0) > 0)
                || weightEntries.length > 0
        };
    }, [order, itemsUpdate, finalTruckWeight, emptyTruckWeight, weightEntries, savedItemStats, isCoilLine]);



    const handleItemFieldChange = (index, field, value) => {
        const str = (typeof value === 'string') ? value : String(value);
        setItemsUpdate(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: str } : item
            )
        );
    };

    // Revise the coils on one line. The picker hands back the whole list on Done, so this
    // replaces it wholesale rather than merging into what was picked at order time.
    const handleCoilPicksChange = (index, picks) => {
        setItemsUpdate(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, coilPicks: Array.isArray(picks) ? picks : [] } : item
            )
        );
    };

    // Save intermediate weight - auto-links items that have quantity filled
    const handleSaveWeightEntry = async () => {
        if (!intermediateWeight || modifiedItemIds.length === 0) {
            setError(formStrings.multiStep?.noItemsToSave || 'Enter truck weight and fill actual quantity for at least one item');
            return;
        }

        if (weightEntries.length >= 10) {
            setError(formStrings.multiStep?.maxEntriesReached || 'Maximum 10 weight entries allowed');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Build items_update for the items being saved in this entry
            const itemsUpdatePayload = modifiedItemIds.map(itemId => {
                const itemIndex = order.items.findIndex(i => i.id === itemId);
                if (itemIndex === -1) return null;

                const item = order.items[itemIndex];
                const update = itemsUpdate[itemIndex];
                const isCoil = isCoilLine(item);
                const coilPicks = isCoil ? (update?.coilPicks || []) : [];
                const actualPieces = parseFloat(String(update?.actualPieces)) || 0;
                const perPieceWeight = parseFloat(String(update?.perPieceWeight)) || 0;
                const sLength = (parseFloat(item.length) || parseFloat(item.standard_length)) || 0;
                const isMeter = !isCoil && item.unit?.toLowerCase() === 'm';
                const isPcs = !isCoil && isPieceUnit(item.unit);

                // A coil line is settled by its coils: the picked kg are the quantity and the
                // coils themselves are the pieces. Never pieces x per-piece weight — no coil
                // has a per-piece weight to multiply by.
                //
                // actual_quantity is the LOADED quantity the backend bills and posts stock
                // against, so it must be in the unit the line is priced in: the piece COUNT
                // for a pcs line, kg otherwise. This path previously sent kg for a pcs line
                // while completeLoading sent the count — the same field in two units — so an
                // order billed after a weight entry was charged price-per-piece x kilograms.
                const actualQty = isCoil
                    ? sumTakeKg(coilPicks)
                    : (isMeter
                        ? (actualPieces * sLength)
                        : (isPcs ? actualPieces : (actualPieces * perPieceWeight)));

                return {
                    id: itemId,
                    actual_pieces: isCoil ? coilPicks.length : (parseInt(update?.actualPieces) || 0),
                    per_piece_weight: isCoil ? 0 : perPieceWeight,
                    actual_quantity: actualQty,
                    // Only a coil line carries the key at all. Omitted means "leave the
                    // picks alone"; an empty list means "this line now has none" and
                    // drops the claims. An ordinary line must never say either.
                    ...(isCoil ? { coil_picks: coilPicksPayload(coilPicks) } : {}),
                    remark: update?.remark || null
                };
            }).filter(Boolean);

            const result = await addWeightEntry(orderId, {
                truck_weight: parseFloat(intermediateWeight),
                item_ids: modifiedItemIds,
                items_update: itemsUpdatePayload,
            });

            if (result.success) {
                setWeightEntries(result.data.weight_entries_list || []);
                setIntermediateWeight('');
                setOrder(result.data);
                // Reset inputs for the items that were just saved. coilPicks is deliberately
                // kept: the line is now banked (and its strip read-only), the calculation
                // stops counting it this cycle, and the list still has to travel with the
                // final completeLoading call.
                setItemsUpdate(prev => prev.map((update, idx) => {
                    const item = order.items[idx];
                    if (modifiedItemIds.includes(item.id)) {
                        return { ...update, actualPieces: '', remark: '' };
                    }
                    return update;
                }));
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err?.message || 'Failed to save weight entry');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset confirmation handlers
    const handleResetClick = () => {
        setShowResetConfirm(true);
    };

    const handleResetConfirm = async () => {
        setShowResetConfirm(false);
        setSubmitting(true);
        setError(null);

        try {
            const result = await resetWeightEntries(orderId);
            if (result.success) {
                setWeightEntries([]);
                setOrder(result.data);
                // Clear all item updates.
                //
                // Coil picks are RESTORED to what the order carries, never emptied: reset
                // undoes the loading stage only. The pick made at order time is an earlier,
                // separate fact — the salesperson chose those specific coils — and wiping it
                // here would quietly destroy their work and leave the line with nothing to
                // load. Seeded from the order the reset just returned, which is the server's
                // post-reset truth.
                const resetItems = Array.isArray(result.data?.items) ? result.data.items : (order?.items || []);
                setItemsUpdate(prev => prev.map((update, idx) => {
                    return {
                        ...update,
                        actualPieces: '',
                        perPieceWeight: update.perPieceWeight || '',
                        coilPicks: picksOfItem(resetItems[idx]),
                    };
                }));
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err?.message || 'Failed to reset weight entries');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResetCancel = () => {
        setShowResetConfirm(false);
    };

    const handleSubmit = async () => {
        setError(null);

        if (!orderId) {
            setError(formStrings.messages.invalidOrder);
            return;
        }

        if (!validateForm()) return;

        setSubmitting(true);

        try {
            const payload = {
                final_truck_weight: parseFloat(finalTruckWeight),
                empty_truck_weight: parseFloat(emptyTruckWeight),
                internal_remark: internalRemark || '',
                items_update: (order.items || []).map((origItem, idx) => {
                    const calcItem = calculatedData.items[idx];
                    const isCoil = isCoilLine(origItem);
                    const isMeter = !isCoil && origItem.unit?.toLowerCase() === 'm';
                    const isPcs = !isCoil && !isMeter && isPieceUnit(origItem.unit);
                    return {
                        id: origItem.id,
                        // A coil line ships exactly what was picked: totalAdjustedW is the sum
                        // of its take_kg (never pro-rated) and totalActualPieces is the number
                        // of coils. The picks travel with it because most trucks are completed
                        // without ever saving an intermediate weight entry, and this call is
                        // then the only place the revised pick reaches the backend.
                        //
                        // A pcs line is priced PER PIECE, and the backend bills it as
                        // price × actual_quantity — so actual_quantity must carry the delivered
                        // PIECE COUNT, not the weight. The weight still travels in actual_weight
                        // (used for the weighbridge/loading cost and the kg stock debit).
                        actual_quantity: isMeter
                            ? calcItem.totalDelivered
                            : (isPcs ? calcItem.totalActualPieces : calcItem.totalAdjustedW),
                        actual_weight: calcItem.totalAdjustedW,
                        actual_pieces: calcItem.totalActualPieces,
                        ...(isCoil
                            ? { coil_picks: coilPicksPayload(itemsUpdate[idx]?.coilPicks) }
                            : {}),
                        remark: itemsUpdate[idx]?.remark || null
                    };
                }),
            };

            const result = await completeLoading(orderId, payload);

            if (result && result.success) {
                if (onCompleted) onCompleted(result.data || {});
                onClose();
            } else {
                setError(result?.error || formStrings.messages.completeFailed);
            }
        } catch (err) {
            console.error('Submit error:', err);
            setError(err?.message || formStrings.messages.completeFailed);
        } finally {
            setSubmitting(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        >
            <div
                className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
                style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-6 border-b shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {formStrings.title}
                        </h2>
                        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {formStrings.subtitle}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors hover:opacity-80"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div
                                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                                style={{
                                    borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5),
                                }}
                            />
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {formStrings.messages.loadingOrder}
                            </p>
                        </div>
                    ) : order ? (
                        <div className="space-y-6">
                            {/* Order Info */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL }}
                                    >
                                        {formStrings.labels.orderId}
                                    </label>
                                    <div
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                    >
                                        {order.order_number || order.id}
                                    </div>
                                </div>

                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                    >
                                        {formStrings.labels.emptyTruckWeight} <span>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={emptyTruckWeight}
                                        onChange={(e) => setEmptyTruckWeight(e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={formStrings.placeholders.emptyTruckWeight}
                                        step="any"
                                        min="0"
                                    />
                                </div>

                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                    >
                                        {formStrings.labels.truckWeight} <span>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={finalTruckWeight}
                                        onChange={(e) => setFinalTruckWeight(e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={formStrings.placeholders.truckWeight}
                                        step="any"
                                        min="0"
                                    />
                                </div>

                                {order.remark && (
                                    <div className="md:col-span-3">
                                        <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                            Order Remark
                                        </label>
                                        <div
                                            className="px-2 py-1.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 mt-1"
                                            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap', width: 'fit-content', minWidth: '200px' }}
                                        >
                                            {order.remark}
                                        </div>
                                    </div>
                                )}
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        Internal Remarks
                                    </label>
                                    <textarea
                                        value={internalRemark}
                                        onChange={(e) => setInternalRemark(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border-2 resize-none text-sm"
                                        rows="2"
                                        placeholder={t('commonForm.internalRemarksOptional')}
                                        style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                                    />
                                </div>
                            </div>


                            {/* Weight Entries Display (only shown when entries exist) */}
                            {weightEntries.length > 0 && (
                                <div
                                    className="p-4 rounded-xl border"
                                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.05) }}
                                >
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                            {formStrings.multiStep?.weightEntriesTitle || 'Weight Entries'} ({weightEntries.length}/10)
                                        </h4>
                                        <button
                                            onClick={handleResetClick}
                                            className="text-sm px-3 py-1 rounded-lg transition-colors hover:opacity-80"
                                            style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                                        >
                                            <FontAwesomeIcon icon={faUndo} className="mr-1" />
                                            {formStrings.multiStep?.resetAll || 'Reset All'}
                                        </button>
                                    </div>
                                    {weightEntries.map((entry) => {
                                        const itemNames = order.items
                                            .filter(i => entry.item_ids.includes(i.id))
                                            .map(i => i.size_name || i.product_name)
                                            .join(', ');

                                        const totalPieces = entry.items_update_details
                                            ? entry.items_update_details.reduce((sum, d) => sum + (d.actual_pieces || 0), 0)
                                            : order.items.filter(i => entry.item_ids.includes(i.id)).reduce((sum, i) => sum + (i.actual_pieces || 0), 0);
                                        const adjPerPiece = totalPieces > 0 ? entry.weight_diff / totalPieces : 0;

                                        return (
                                            <div key={entry.entry_id} className="p-3 bg-white rounded-lg mb-2 border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                        {formStrings.multiStep?.stepLabel || 'Step'} {entry.entry_id}: {entry.truck_weight} kg
                                                    </span>
                                                    <span className="font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                                        +{entry.weight_diff.toFixed(2)} kg
                                                    </span>
                                                </div>
                                                <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                    {entry.item_ids.length} {formStrings.multiStep?.itemsCountLabel || 'items'}: {itemNames}
                                                </div>
                                                <div className="text-xs mt-1 font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                    Adjusted per piece weight: {adjPerPiece.toFixed(3)} kg/pc ({totalPieces} pcs)
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Items Section */}
                            <div>
                                <h3
                                    className="text-lg font-semibold mb-4 flex items-center gap-3"
                                    style={{ color: UI_COLORS.TEXT.PRIMARY }}
                                >
                                    {formStrings.labels.items}
                                </h3>

                                {!Array.isArray(order.items) || order.items.length === 0 ? (
                                    <div
                                        className="text-center py-8 text-sm"
                                        style={{ color: UI_COLORS.TEXT.SECONDARY }}
                                    >
                                        {formStrings.messages.noItems}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {calculatedData.items.map((item, index) => {
                                            const isModified = modifiedItemIds.includes(item.id);
                                            const isFullyLoaded = item.savedPieces > 0;
                                            // Coil lines: pieces are the coils themselves and there is no
                                            // per-piece weight, so both fields stop being inputs here.
                                            const isCoil = item.isCoil;
                                            // A pcs line is priced per piece, so the delivered PIECE count is
                                            // the figure that matters (and is what gets billed). Flag it so the
                                            // operator is told plainly to enter pieces, not weight.
                                            const isPcs = !isCoil && isPieceUnit(item.unit);
                                            const coilPicks = itemsUpdate[index]?.coilPicks || [];
                                            const coilProductSizeId = item.product_size_id ?? item.size_id ?? null;
                                            // What is still to load on this line, so the strip's short/over
                                            // badge reads against this cycle and not the whole order.
                                            const coilTargetKg = Math.max(0, (item.totalTarget || 0) - (item.savedWeight || 0));

                                            return (
                                            <div
                                                key={item.id || index}
                                                className={`grid grid-cols-1 md:grid-cols-6 gap-4 p-4 rounded-xl border-2 transition-all`}
                                                style={{
                                                    backgroundColor: isFullyLoaded ? '#f0f9ff' : UI_COLORS.BACKGROUNDS.SECTION,
                                                    borderColor: isFullyLoaded ? '#bae6fd' : (isModified ? UI_COLORS.STATUS.WARNING : UI_COLORS.BORDERS.LIGHT),
                                                    opacity: isFullyLoaded ? 0.9 : 1
                                                }}
                                            >
                                                {/* Product Name */}
                                                <div className="md:col-span-2">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <label
                                                            className="text-xs font-semibold uppercase tracking-wider"
                                                            style={{ color: UI_COLORS.TEXT.MUTED }}
                                                        >
                                                            {formStrings.labels.productName}
                                                        </label>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="text-sm font-bold"
                                                                style={{ color: UI_COLORS.TEXT.PRIMARY }}
                                                            >
                                                                {item.size_name || item.product_name || formStrings.labels.unknownProduct}
                                                            </div>
                                                            {item.load_by_pieces && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('common.formFields.labels.loadByPieces')}</span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {formStrings.labels.unitLabel}: {item.unit}, {formStrings.labels.lengthLabel}: {(parseFloat(item.length) || parseFloat(item.standard_length)) || 0} {item.unit}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Loading-by-pieces banner: this line is captured and billed by
                                                    PIECE COUNT, so make that explicit at the point of entry. */}
                                                {(isPcs || item.load_by_pieces) && !isCoil && (
                                                    <div
                                                        className="md:col-span-6 flex items-start gap-2 px-3 py-2 rounded-lg text-xs font-medium"
                                                        style={{
                                                            color: UI_COLORS.STATUS.INFO,
                                                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                                                            border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.25)}`,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} className="mt-0.5" />
                                                        <span>
                                                            {isPcs
                                                                ? t('pieces.loading.pcsUnitNote')
                                                                : t('pieces.loading.loadByPiecesNote')}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Quantity (view-only) */}
                                                <div>
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="text-sm font-medium" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                                            {formStrings.labels.quantity}
                                                        </label>
                                                    </div>
                                                    <div
                                                        className="w-full px-3 py-2 rounded-lg border transition-colors bg-gray-50 text-gray-700 font-medium"
                                                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                                                    >
                                                        {item.totalTarget} {item.unit}
                                                    </div>
                                                </div>

                                                {/* Required Pieces (view-only) */}
                                                <div>
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="text-sm font-medium" style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}>
                                                            Ordered Pieces
                                                        </label>
                                                    </div>
                                                    <div
                                                        className="w-full px-3 py-2 rounded-lg border transition-colors bg-gray-50 text-gray-700 font-medium"
                                                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                                                    >
                                                        {item.quantity_in_pieces ?? '-'}
                                                    </div>
                                                </div>

                                                {/* Actual Pieces — a coil line's "pieces" are its coils, so the
                                                    count follows the pick and is never typed. */}
                                                <div>
                                                    <label
                                                        className="block text-sm font-medium mb-2"
                                                        style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                                    >
                                                        {formStrings.labels.actualPieces} {!isCoil && <span>*</span>}
                                                    </label>
                                                    {isCoil ? (
                                                        <div
                                                            className="w-full px-3 py-2 rounded-lg border transition-colors bg-gray-50 text-gray-700 font-medium"
                                                            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                                                        >
                                                            {isFullyLoaded ? item.savedPieces : coilPicks.length}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            pattern="[0-9]*"
                                                            value={isFullyLoaded ? item.savedPieces : (itemsUpdate[index]?.actualPieces ?? '')}
                                                            onChange={(e) => handleItemFieldChange(index, 'actualPieces', e.target.value)}
                                                            disabled={isFullyLoaded}
                                                            className="w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            style={{
                                                                backgroundColor: isFullyLoaded ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                                color: isFullyLoaded ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                                            }}
                                                            placeholder="0"
                                                        />
                                                    )}
                                                </div>

                                                {/* Per Piece Weight — meaningless for a coil: every coil weighs
                                                    what it weighs, so the field is not shown at all. */}
                                                {!isCoil && (
                                                    <div>
                                                        <label
                                                            className="block text-sm font-medium mb-2"
                                                            style={{ color: UI_COLORS.FORMS.LABEL, whiteSpace: 'nowrap' }}
                                                        >
                                                            {formStrings.labels.perPieceWeight} <span>*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                            value={itemsUpdate[index]?.perPieceWeight ?? ''}
                                                            onChange={(e) => handleItemFieldChange(index, 'perPieceWeight', e.target.value)}
                                                            disabled={isFullyLoaded}
                                                            className="w-full px-3 py-2 rounded-lg border transition-colors focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            style={{
                                                                backgroundColor: isFullyLoaded ? '#f8fafc' : UI_COLORS.FORMS.INPUT.background,
                                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                                color: isFullyLoaded ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
                                                            }}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                )}

                                                {/* Coils on the truck — the pick made at order time, revisable
                                                    here until the line is banked in a weight entry. */}
                                                {isCoil && (
                                                    <div className="md:col-span-6">
                                                        <label
                                                            className="block text-xs font-semibold uppercase tracking-wider mb-2"
                                                            style={{ color: UI_COLORS.TEXT.MUTED }}
                                                        >
                                                            {t('coils.picker.context.loading')}
                                                        </label>
                                                        <CoilPickStrip
                                                            picks={coilPicks}
                                                            targetKg={coilTargetKg}
                                                            warehouseId={order.warehouse_id}
                                                            productSizeId={coilProductSizeId}
                                                            disabled={!order.warehouse_id || !coilProductSizeId}
                                                            onChange={(picks) => handleCoilPicksChange(index, picks)}
                                                            context="loading"
                                                            readOnly={isFullyLoaded}
                                                        />
                                                    </div>
                                                )}

                                                {/* Remark */}
                                                <div className="md:col-span-4">
                                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                                        {formStrings.labels.remark}
                                                    </label>
                                                        <textarea
                                                            rows="3"
                                                            value={itemsUpdate[index]?.remark ?? ''}
                                                            onChange={(e) => handleItemFieldChange(index, 'remark', e.target.value)}
                                                            disabled={isFullyLoaded}
                                                            className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 focus:shadow-md disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                                                            style={{
                                                                backgroundColor: isFullyLoaded ? '#f8fafc' : '#fef9c3',
                                                                borderColor: (itemsUpdate[index]?.remark && !isFullyLoaded) ? '#fde047' : UI_COLORS.FORMS.INPUT.border,
                                                                color: isFullyLoaded ? UI_COLORS.TEXT.MUTED : UI_COLORS.TEXT.PRIMARY,
                                                                display: '-webkit-box', 
                                                                WebkitLineClamp: 3, 
                                                                WebkitBoxOrient: 'vertical', 
                                                                overflow: 'hidden'
                                                            }}
                                                            placeholder={formStrings.placeholders.remark}
                                                        />
                                                </div>

                                                {/* Approx Weight & Cost (Calculated) */}
                                                <div className="md:col-span-6 flex flex-col justify-center bg-white p-3 rounded border text-sm">
                                                    {(() => {
                                                        const calcData = calculatedData.items[index];
                                                        if (!calcData) return null;

                                                        const currentPieces = calcData.currentActualPieces || 0;
                                                        const adjustedWeight = isFullyLoaded
                                                            ? (item.savedWeight || 0)
                                                            : (calcData.adjustedW || 0);
                                                        const adjPiecesCount = isFullyLoaded ? item.savedPieces : currentPieces;
                                                        const adjPerPiece = adjPiecesCount > 0 ? adjustedWeight / adjPiecesCount : null;

                                                        return (
                                                            <>
                                                                <div className="flex justify-between font-bold mt-1" style={{ color: UI_COLORS.TEXT.BLACK }}>
                                                                    <span>{t('common.formFields.labels.totalLoaded')}</span>
                                                                    {/* totalDelivered is a PIECE COUNT for a pcs line (see the cross-cycle
                                                                                    block above), so render it as a whole number — "100.000 pcs"
                                                                                    reads like a weight. */}
                                                                    <span>{isPieceUnit(item.unit) ? Math.round(calcData.totalDelivered || 0) : calcData.totalDelivered?.toFixed(3)} {item.unit}</span>
                                                                </div>
                                                                <hr className="my-2 border-gray-100" />
                                                                {/* No per-piece weight on a coil line — each coil is weighed
                                                                    on its own, so an average kg/pc says nothing. */}
                                                                {!isCoil && (
                                                                    <div className="flex justify-between mt-1">
                                                                        <span className="text-gray-500">{t('common.formFields.labels.adjustedPerPieceWeight')}</span>
                                                                        <span className="font-medium">{adjPerPiece !== null ? `${adjPerPiece.toFixed(3)} kg/pc` : '— kg/pc'}</span>
                                                                    </div>
                                                                )}
                                                                <div className="flex justify-between mt-1" style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                                                    <span className="">{t('common.formFields.labels.adjustedWeight')}</span>
                                                                    <span className="font-medium">{adjustedWeight.toFixed(2)} kg</span>
                                                                </div>
                                                                <div className="flex justify-between mt-1">
                                                                    <span className="text-gray-500">{t('common.formFields.labels.loadingCost')}</span>
                                                                    <span className="font-medium text-gray-900">{t('common.symbols.currency')} {calcData.cost?.toFixed(2)}</span>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            );
                                        })}
                                        <div className="flex justify-end mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                            <div className="text-right">
                                                <div className="text-sm text-gray-500">{formStrings.labels.totalLoadingCost}</div>
                                                <div className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.BLACK}}>
                                                    {t('common.symbols.currency')} {calculatedData.totalCost.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Save Intermediate Weight Section (Optional) */}
                            <div
                                className="p-4 rounded-xl border-2 border-dashed"
                                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.4), backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.03) }}
                            >
                                <h4 className="font-semibold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                    {formStrings.multiStep?.saveIntermediateWeight || 'Save Intermediate Weight (Optional)'}
                                </h4>
                                <p className="text-sm mb-3" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    {modifiedItemIds.length > 0
                                        ? (formStrings.multiStep?.itemsToSave || '{n} item(s) will be linked to this weight entry').replace('{n}', modifiedItemIds.length)
                                        : (formStrings.multiStep?.noItemsToSave || 'Fill actual quantity for items to save them')}
                                </p>
                                <div className="flex gap-4 items-end">
                                    <div className="flex-1">
                                        <label
                                            className="block text-sm font-medium mb-2"
                                            style={{ color: UI_COLORS.FORMS.LABEL }}
                                        >
                                            {formStrings.multiStep?.truckWeightLabel || 'Truck Weight'} (kg)
                                        </label>
                                        <input
                                            type="number"
                                            value={intermediateWeight}
                                            onChange={(e) => setIntermediateWeight(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            style={{
                                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                color: UI_COLORS.FORMS.INPUT.text,
                                            }}
                                            placeholder={formStrings.multiStep?.intermediateWeightPlaceholder || 'Enter current truck weight'}
                                            step="any"
                                            min="0"
                                        />
                                    </div>
                                    <button
                                        onClick={handleSaveWeightEntry}
                                        disabled={!intermediateWeight || modifiedItemIds.length === 0 || weightEntries.length >= 10 || submitting}
                                        className="px-6 py-3 rounded-xl font-semibold shadow transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            backgroundColor: UI_COLORS.BRAND.PRIMARY,
                                            color: (intermediateWeight && modifiedItemIds.length > 0) ? '#000' : UI_COLORS.TEXT.MUTED,
                                        }}
                                    >
                                        <FontAwesomeIcon 
                                            icon={faSave} 
                                            className="mr-2" 
                                            style={{ color: (intermediateWeight && modifiedItemIds.length > 0) ? '#000' : UI_COLORS.TEXT.MUTED }}
                                        />
                                        {formStrings.multiStep?.saveButton || 'Save'} ({modifiedItemIds.length})
                                    </button>
                                </div>
                                {weightEntries.length >= 10 && (
                                    <p className="text-xs mt-2" style={{ color: UI_COLORS.STATUS.WARNING }}>
                                        {formStrings.multiStep?.maxEntriesReached || 'Maximum 10 weight entries allowed'}
                                    </p>
                                )}
                            </div>
                            {/* Error Modal */}
                            <ConfirmDialog
                                isOpen={!!error}
                                title={t('common.messages.error')}
                                message={error}
                                confirmLabel={t('common.buttons.ok')}
                                cancelLabel={null}
                                onConfirm={() => setError(null)}
                                confirmVariant="danger"
                            />
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {error || formStrings.messages.invalidOrder}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-t shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-yellow-100 border border-yellow-200">
                        <span className="text-xs font-semibold uppercase tracking-wider text-yellow-700">{t('common.formFields.labels.weight')}</span>
                        <span className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {(calculatedData.totalActualQty || 0).toFixed(2)} kg
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-medium transition-colors"
                        disabled={submitting}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                            color: UI_COLORS.BUTTONS.SECONDARY.text,
                            opacity: submitting ? 0.6 : 1,
                        }}
                    >
                        {formStrings.buttons.cancel}
                    </button>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={submitting || loading || !order || !calculatedData.isValid}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                            color: UI_COLORS.BUTTONS.PRIMARY.text,
                        }}
                    >
                        <span className="inline-flex items-center gap-2">
                            {submitting && (
                                <span className="animate-spin">
                                    <div
                                        className="h-4 w-4 rounded-full border-2 border-b-transparent"
                                        style={{
                                            borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6),
                                        }}
                                    />
                                </span>
                            )}
                            {!submitting && <FontAwesomeIcon icon={faCheck} />}
                            <span>
                                {submitting
                                    ? formStrings.buttons.completing
                                    : formStrings.buttons.complete}
                            </span>
                        </span>
                    </button>
                    </div>
                </div>
            </div>

            {/* Reset Confirmation Dialog */}
            {showResetConfirm && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                    style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
                >
                    <div
                        className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full"
                        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
                    >
                        <h4 className="font-semibold text-lg mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {formStrings.multiStep?.confirmResetTitle || 'Confirm Reset'}
                        </h4>
                        <p className="mb-4" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {formStrings.multiStep?.confirmResetMessage || 'Are you sure you want to reset all weight entries? This will unlock all items and clear their values.'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleResetCancel}
                                className="px-4 py-2 rounded-xl font-medium transition-colors"
                                style={{
                                    backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                                    color: UI_COLORS.BUTTONS.SECONDARY.text,
                                }}
                            >
                                {formStrings.multiStep?.cancelButton || 'Cancel'}
                            </button>
                            <button
                                onClick={handleResetConfirm}
                                disabled={submitting}
                                className="px-4 py-2 rounded-xl font-semibold transition-colors disabled:opacity-70"
                                style={{
                                    backgroundColor: UI_COLORS.STATUS.ERROR,
                                    color: '#fff',
                                }}
                            >
                                {formStrings.multiStep?.confirmResetButton || 'Reset'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompleteTruckLoading;
